import React, { Dispatch, FC, ReactNode, SetStateAction, useEffect, useState } from 'react'

import { CustomPopup, CustomPopupProps } from '../../../components/modals/CustomPopup'
import { FeedbotSuspense } from '../../../components/design-system/FeedbotSuspense'
import { ChasingDotsLoader } from '../../../components/design-system/ChasingDotsLoader'

export type TabControls = [string, React.Dispatch<React.SetStateAction<string>>]
type InjectedProps<ResolveVal = void> = Pick<CustomPopupProps, 'open'> & {
  state: ResolveVal
  setState: Dispatch<SetStateAction<ResolveVal>>
  tabControls: TabControls
  onClose(resolveVal?: ResolveVal): void
  initialState?: any
}

export type GetProps<ResolveVal = void> = (
  injected: InjectedProps<ResolveVal>
) => Omit<CustomPopupProps, 'onClose' | 'open' | 'initialState'>

export type DispatchConfig = { initialState?: any; initialTab?: string }
export type ModalDispatcher = ReturnType<typeof useModalQueue>['dispatchModal']

// This allows us to dispatch modals imperatively without having to worry about state, similarly to bootbox API
export const ImperativeModalProvider: FC<{ subscribe(dispatch: ModalDispatcher): void }> = ({
  children,
  subscribe
}) => {
  const { dispatchModal, modalQueue } = useModalQueue()

  useEffect(() => subscribe(dispatchModal), [])

  return (
    <>
      {children}
      <FeedbotSuspense name="SelectPortalProvider" fallback={<></>}>
        {modalQueue[modalQueue.length - 1]?.layout}
      </FeedbotSuspense>
    </>
  )
}

// We separate into a hook, so we can infer dispatchModal type from the return value
function useModalQueue() {
  const [modalQueue, setModalQueue] = useState<{ layout: ReactNode; uniqueRef: object }[]>([])

  function dispatchCore<T>(getProps: GetProps<T>, config: DispatchConfig = {}) {
    return new Promise<T>((res, rej) => {
      setModalQueue(prev => {
        const uniqueRef = {}

        const onClose = (resolveValue?: T) => {
          setModalQueue(prev => prev.filter(i => i.uniqueRef !== uniqueRef))
          res(resolveValue!)
        }

        const descriptor = {
          layout: (
            <WrappedPopup
              getProps={getProps}
              closeModal={onClose}
              initialSate={config.initialState}
              initialTab={config.initialTab}
            />
          ),
          uniqueRef: uniqueRef
        }

        return [...prev, descriptor]
      })
    })
  }

  // Two overloads - a plain dispatch, and a curried one which allows providing config when needed
  function dispatch<ResVal>(getProps: GetProps<ResVal>): Promise<ResVal>
  function dispatch(config: DispatchConfig): <ResVal>(getProps: GetProps<ResVal>) => Promise<ResVal>
  function dispatch<ResVal>(
    p1: GetProps<ResVal> | DispatchConfig
  ): Promise<ResVal> | (<T>(getProps: GetProps<T>) => Promise<T>) {
    if (typeof p1 === 'function') {
      return dispatchCore<ResVal>(p1)
    } else {
      return <T,>(getProps: GetProps<T>) => dispatchCore<T>(getProps, p1)
    }
  }

  return { dispatchModal: dispatch, modalQueue }
}

const WrappedPopup: FC<{
  getProps: GetProps<any>
  initialSate?: any
  initialTab?: string
  closeModal: (resolveValue?: any) => void
}> = ({ getProps, closeModal, initialSate = {}, initialTab = '' }) => {
  const [state, setState] = useState(initialSate)
  const tabControls = useState(initialTab) // Managing tabControls here allows tab management even when dispatched out of components

  const props = getProps({ onClose: closeModal, state, setState, tabControls })

  return (
    <FeedbotSuspense name="SelectPortalProvider" fallback={<></>}>
      <CustomPopup
        {...props}
        children={
          <FeedbotSuspense name="SelectPortalProvider" fallback={<ChasingDotsLoader />}>
            {props.children}
          </FeedbotSuspense>
        }
        onClose={() => closeModal()}
        open
      />
    </FeedbotSuspense>
  )
}
